"use client";

import { getSafeRedirectRedirect, toSafeAppRedirect } from "@/lib/safe-redirect";
import { clearAuthIntent, getAuthIntent } from "@/lib/auth-intent";
import { resolvePostAuthTarget, type ContextMode } from "@/lib/post-auth-policy";

const AUTH_STORAGE_KEY = "skillstorm_auth";
const ACADEMIC_YEAR_STORAGE_KEY = "skillstorm_academic_year";
const ACTIVE_MEMBERSHIP_STORAGE_KEY = "skillstorm_activeMembershipId";
const ACTIVE_MEMBERSHIP_SWITCHED_AT_STORAGE_KEY = "skillstorm_activeMembershipSwitchedAt";
const RETURN_URL_STORAGE_KEY = "returnUrl";
const PROGRESS_OFFLINE_DB = "skillstorm-offline";
let logoutNavigationInProgress = false;

export const markLogoutNavigationInProgress = (): void => {
  logoutNavigationInProgress = true;
};

export const clearLogoutNavigationInProgress = (): void => {
  logoutNavigationInProgress = false;
};

export const isLogoutNavigationInProgress = (): boolean => logoutNavigationInProgress;

function clearPrivateOfflineArtifacts(): void {
  if (typeof window === "undefined") return;

  if ("indexedDB" in window) {
    // Logout is a hard privacy boundary on shared school devices. We delete the
    // whole private progress DB, including any legacy unscoped rows.
    window.indexedDB.deleteDatabase(PROGRESS_OFFLINE_DB);
  }

  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker.ready
      .then((registration) => {
        registration.active?.postMessage({ type: "CLEAR_PRIVATE_CACHES" });
      })
      .catch(() => undefined);
  }
}

export const clearClientSessionArtifacts = (): void => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    window.localStorage.removeItem(ACADEMIC_YEAR_STORAGE_KEY);
    window.localStorage.removeItem(ACTIVE_MEMBERSHIP_STORAGE_KEY);
    window.localStorage.removeItem(ACTIVE_MEMBERSHIP_SWITCHED_AT_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    clearPrivateOfflineArtifacts();
  }

  if (typeof document !== "undefined") {
    document.cookie = "ss_csrf=; Max-Age=0; Path=/; SameSite=Lax";
  }
};

export const storeReturnUrl = (path: string): void => {
  if (typeof window === "undefined") return;
  const target = toSafeAppRedirect(path);
  if (!target) return;
  try {
    window.sessionStorage.setItem(RETURN_URL_STORAGE_KEY, target);
  } catch {
    // The explicit URL also carries the destination when storage is unavailable.
  }
};

export const readReturnUrl = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
    return value ? toSafeAppRedirect(value) : null;
  } catch {
    return null;
  }
};

export const clearReturnUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
  } catch {
    // Storage may be unavailable in a private browser.
  }
};

/** Use the same policy for password login, GIS and the password-change detour. */
export function resolveAuthReturnTarget(currentPath: string, contextMode: ContextMode): string | null {
  const safePath = toSafeAppRedirect(currentPath) ?? "/";
  return resolvePostAuthTarget({
    currentPath: safePath,
    searchParams: new URL(safePath, "https://return.invalid").searchParams,
    contextMode,
    authIntent: getAuthIntent(),
    storedReturnUrl: readReturnUrl(),
  });
}

/** Capture a protected destination before a mandatory auth step changes the URL. */
export function preserveAuthReturnTarget(currentPath: string, contextMode: ContextMode): string | null {
  const safePath = toSafeAppRedirect(currentPath) ?? "/";
  const url = new URL(safePath, "https://return.invalid");
  const target = getSafeRedirectRedirect(url.searchParams) ??
    (url.pathname === "/join" || /^\/app(?:\/|$)/.test(url.pathname)
      ? safePath
      : resolveAuthReturnTarget(safePath, contextMode));
  if (target) storeReturnUrl(target);
  return target;
}

/** Query handoff survives a reload or disabled sessionStorage; no external URLs. */
export function authStepHref(step: "/login" | "/change-password" | "/reset-password", target: string | null): string {
  const safe = target ? toSafeAppRedirect(target) : null;
  return safe ? `${step}?${new URLSearchParams({ from: safe }).toString()}` : step;
}

export function clearAuthReturnTarget(): void {
  clearReturnUrl();
  clearAuthIntent();
}

/** Explicit account change clears private session data, then retains only its destination. */
export async function signOutForAuthReturn(logout: (options?: { returnTo?: string | null }) => Promise<void>, target: string | null): Promise<string> {
  const safe = target ? toSafeAppRedirect(target) : null;
  // The logout owner must use this destination for every navigation path,
  // including its hard-navigation fallback and any intervening route guards.
  await logout({ returnTo: safe });
  clearAuthReturnTarget();
  if (safe) storeReturnUrl(safe);
  return authStepHref("/login", safe);
}
